import type { CropId, Inventory, Vec2 } from "./types";
import { clamp, distance } from "./math";
import { confineWorld, waypoint } from "./regions";

export const WELL: Vec2 = { x: 520, y: 525 };
export const CAMP: Vec2 = { x: 805, y: 635 };
export const GATE: Vec2 = { x: 1125, y: 465 };
export const REACH = 83;
export type UpgradeId = "tools" | "soil" | "capacity" | "sprinkler";
export interface Progress {
  harvests: Record<CropId, number>;
  watered: number;
  awakened: number;
  gathered: number;
  crafted: number;
  victories: number;
  quest: number;
  coins: number;
  tools: number;
  soil: number;
  capacity: number;
  order: number;
  orderBaseline: number;
}
export const emptyCrops = (): Record<CropId, number> => ({
  pumpkin: 0,
  coconut: 0,
  wintermelon: 0,
  banana: 0,
  moonherb: 0,
  carrot: 0,
  chili: 0,
});
export const newProgress = (): Progress => ({
  harvests: emptyCrops(),
  watered: 0,
  awakened: 0,
  gathered: 0,
  crafted: 0,
  victories: 0,
  quest: 0,
  coins: 8,
  tools: 0,
  soil: 0,
  capacity: 0,
  order: 0,
  orderBaseline: 0,
});
export interface Recipe {
  name: string;
  note: string;
  cost: Partial<Inventory>;
  max: number;
}
export const RECIPES: Record<UpgradeId, Recipe> = {
  tools: {
    name: "藤纹法杖",
    note: "每级攻击 +7，采集伤害 +10",
    cost: { wood: 6, stone: 3 },
    max: 3,
  },
  soil: {
    name: "沃土秘方",
    note: "所有作物每级生长 +15%，新播种肥力提高",
    cost: { wood: 4, fertilizer: 2 },
    max: 2,
  },
  capacity: {
    name: "灵契扩容",
    note: "伙伴容量 +2，上限 8 位",
    cost: { wood: 6, stone: 6, islandDust: 3 },
    max: 2,
  },
  sprinkler: {
    name: "灵田滴灌",
    note: "当前灵田自动取泉水，永久维持适宜水分",
    cost: { wood: 4, stone: 4 },
    max: 1,
  },
};

/** 先验证整笔交易，后统一扣款，避免部分扣除或负库存。 */
export function pay(
  inventory: Inventory,
  cost: Partial<Inventory>,
  multiplier = 1,
): boolean {
  const entries = Object.entries(cost) as Array<[keyof Inventory, number]>;
  if (
    entries.some(
      ([key, value]) =>
        !Number.isFinite(value) ||
        value < 0 ||
        inventory[key] < value * multiplier,
    )
  )
    return false;
  for (const [key, value] of entries) inventory[key] -= value * multiplier;
  return true;
}

export function confine(pos: Vec2, margin = 20): void {
  confineWorld(pos, margin);
}

export function approach(
  pos: Vec2,
  target: Vec2,
  speed: number,
  dt: number,
  stop = 0,
): Vec2 {
  const routed = waypoint(pos, target);
  if (routed !== target) {
    target = routed;
    stop = 0;
  }
  const gap = distance(pos, target);
  const travel = Math.min(Math.max(0, gap - stop), speed * dt);
  const ratio = gap ? travel / gap : 0;
  const velocity = {
    x: ((target.x - pos.x) * ratio) / Math.max(dt, 0.001),
    y: ((target.y - pos.y) * ratio) / Math.max(dt, 0.001),
  };
  pos.x += velocity.x * dt;
  pos.y += velocity.y * dt;
  confine(pos);
  return velocity;
}

export function stickVector(dx: number, dy: number, radius = 48): Vec2 {
  const magnitude = Math.hypot(dx, dy);
  if (magnitude < radius * 0.12) return { x: 0, y: 0 };
  const strength = clamp(magnitude / radius, 0, 1);
  return { x: (dx / magnitude) * strength, y: (dy / magnitude) * strength };
}

/** 小屏采用可滚动相机窗口，而不是把整个 1600 像素世界缩成一条横带。 */
export function worldScale(width: number, height: number): number {
  return Math.max(0.65, Math.min(1.35, Math.min(width / 1280, height / 650)));
}
